import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["container"]

  connect() {
    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create("NotificationsChannel", {
      received: (data) => this.showToast(data)
    })
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
    if (this.consumer) this.consumer.disconnect()
  }

  showToast(data) {
    // The contact page handles its own contact's verdict with the fix modal
    // (email_verification_controller.js); a toast on top would say it twice.
    if (data.contact_id && document.body.dataset.watchingContactId === String(data.contact_id)) {
      document.dispatchEvent(new CustomEvent("email-verification:verdict", { detail: data }))
      return
    }

    const toast = document.createElement("div")
    toast.className = "toast-notification transform translate-x-full transition-transform duration-300 ease-out"
    toast.setAttribute("role", "alert")

    const isError = data.type === "email_invalid"
    const isWarning = data.type === "email_risky"
    const borderColor = isError ? "#E57373" : isWarning ? "#F5A623" : "#9CAF88"
    const iconColor = isError ? "#E57373" : isWarning ? "#F5A623" : "#9CAF88"

    let icon = ""
    if (isError) {
      icon = `<svg class="w-5 h-5 flex-shrink-0" fill="${iconColor}" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>`
    } else if (isWarning) {
      icon = `<svg class="w-5 h-5 flex-shrink-0" fill="${iconColor}" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>`
    } else {
      icon = `<svg class="w-5 h-5 flex-shrink-0" fill="${iconColor}" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>`
    }

    let actionHtml = ""
    if (data.link) {
      const target = data.link_target || "_self"
      actionHtml = `<a href="${this.esc(data.link)}" target="${target}" class="inline-flex items-center mt-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#C4A484] text-white hover:bg-[#B3936E] transition-colors">${this.esc(data.link_text || "View")}</a>`
    }

    let extraInfo = ""
    if (data.other_candidates && data.other_candidates > 0) {
      extraInfo = `<p class="text-xs text-[#8C8C8C] mt-1">${data.other_candidates} other email candidate${data.other_candidates > 1 ? 's' : ''} available for review.</p>`
    }

    toast.innerHTML = `
      <div class="flex items-start gap-3 p-4 bg-white rounded-lg shadow-lg border border-[#E8E4DF] max-w-sm" style="border-left: 4px solid ${borderColor}">
        <div class="flex-shrink-0 mt-0.5">${icon}</div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-[#2D2D2D]">${this.esc(data.title || "Notification")}</p>
          <p class="text-sm text-[#8C8C8C] mt-0.5">${this.esc(data.message || "")}</p>
          ${extraInfo}
          ${actionHtml}
        </div>
        <button class="flex-shrink-0 text-[#8C8C8C] hover:text-[#2D2D2D] transition-colors" data-action="click->toast#dismiss" data-toast-id="${toast.id}">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
    `

    this.containerTarget.appendChild(toast)

    // Animate in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.classList.remove("translate-x-full")
        toast.classList.add("translate-x-0")
      })
    })

    // Auto-dismiss for non-error toasts (warning = 10s, info = 8s)
    if (!isError) {
      const duration = isWarning ? 10000 : 8000
      setTimeout(() => this.removeToast(toast), duration)
    }
  }

  dismiss(event) {
    const toast = event.currentTarget.closest(".toast-notification")
    if (toast) this.removeToast(toast)
  }

  removeToast(toast) {
    toast.classList.remove("translate-x-0")
    toast.classList.add("translate-x-full")
    setTimeout(() => toast.remove(), 300)
  }

  esc(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }
}
